import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "fill-height d-flex flex-column justify-lg-space-between" }
const _hoisted_2 = { class: "editor d-flex fill-height" }
const _hoisted_3 = {
  key: 0,
  class: "monaco-loading d-flex justify-content-center align-items-center"
}
const _hoisted_4 = { class: "monaco-error alert alert-danger m-3" }
const _hoisted_5 = { class: "mb-0" }
const _hoisted_6 = ["id"]

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { isEqual } from 'underscore'
import hotkeys from 'hotkeys-js'

// Types only - no actual import
import type * as monacoType from 'monaco-editor'

// Import stores and utilities
import { qEditorState } from '../stores'
import { mathliveState } from '../stores/mathlive'
import { editorEventBus } from '../stores/eventBus'
import { CursoPosition } from '../types'
import { mathLiveToLatex, latexToMathLive, detectMath } from '../../../lib/mathlive'

import ImageApi from '../api/ImageAPI'
import QuestionType from '../lib/QuestionType'
import Question from '../models/Question'
import { QUESTION_SURVEY_TYPES, QUESTION_TEST_TYPES } from '../const/question-types'


export default /*@__PURE__*/_defineComponent({
  __name: 'MonacoLazyComponent',
  props: {
    editorId: {},
    enableAiInlineEdit: { type: Boolean, default: false }
  },
  emits: ["editor-ready"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

// Template ref
const editorContainer = ref<HTMLElement | null>(null)

// Loading state
const loading = ref(true)
const error = ref<string | null>(null)

// Monaco instances (loaded lazily)
let monaco: typeof monacoType | null = null
let editor: monacoType.editor.IStandaloneCodeEditor | null = null
let autoCompleterDisposable: monacoType.IDisposable | null = null
let aiInlineEditInstance: any = null

// Imported modules
let MonacoAutoCompleter: any = null
let MonacoIndentProcess: any = null
let AIInlineEdit: any = null
let registerQsyntaxLanguage: any = null
let QSYNTAX_LANGUAGE_ID: string = 'qsyntax'

let skipUpdateContent: 'skip_dispatch' | 'skip_update' | 'none' | 'skip_all' = 'none'
let beforeCursoPosition: CursoPosition | null = null
let markers: string[] = []

// AI Generation
const promptForQuestions = ref<any[]>([])
const selectedQuestionPromptId = ref<number | null>(null)
const instructionQuestionForUser = ref<string>('')

const exam = computed(() => qEditorState.exam)
const body = computed(() => qEditorState.body)
const questions = computed(() => qEditorState.questions)
const skills = computed(() => qEditorState.skills)

const cursoPosition = computed(() => qEditorState.cursoPosition)
const currentPage = computed(() => qEditorState.displayCurrentPage)
const isRevisionMode = computed(() => qEditorState.isRevisionMode)

// created() logic - parse prompts data synchronously at setup time
try {
  const editorData = document.getElementById("question_editor_data")
  if (editorData?.dataset.prompts) {
    promptForQuestions.value = JSON.parse(editorData.dataset.prompts)
      .filter((prompt: any) => prompt.category == "auto_question")
  }
} catch (e) {
  console.warn('Failed to parse prompts data', e)
}

onMounted(async () => {
  editorEventBus.on('focus', focus)
  await loadMonacoEditor()
  setupHotKeys()
})

onBeforeUnmount(() => {
  removeHotkeys()
  disposeEditor()
})

watch(selectedQuestionPromptId, (val) => {
  const selectedPrompt = promptForQuestions.value.find((prompt: any) => prompt.id == val)
  instructionQuestionForUser.value = selectedPrompt ? selectedPrompt.instruction_for_user : ""
})



/**
 * Lazy load Monaco Editor and all related modules
 */
const loadMonacoEditor = async () => {
  loading.value = true
  error.value = null

  try {
    // Dynamic import of Monaco and related modules
    const [monacoModule, monacoModules] = await Promise.all([
      import('monaco-editor'),
      import('../lib/monaco')
    ])

    monaco = monacoModule
    MonacoAutoCompleter = monacoModules.MonacoAutoCompleter
    MonacoIndentProcess = monacoModules.MonacoIndentProcess
    AIInlineEdit = monacoModules.AIInlineEdit
    registerQsyntaxLanguage = monacoModules.registerQsyntaxLanguage
    QSYNTAX_LANGUAGE_ID = monacoModules.QSYNTAX_LANGUAGE_ID

    // Setup editor after modules loaded
    await nextTick()
    setupMonacoEditor()
    loading.value = false

  } catch (err: any) {
    console.error('Failed to load Monaco Editor:', err)
    error.value = err.message || 'Unknown error loading editor'
    loading.value = false
  }
}

const retryLoad = async () => {
  await loadMonacoEditor()
}

const setupHotKeys = () => {
  removeHotkeys()
  hotkeys.filter = () => true
  hotkeys('alt+m', 'editor', () => {
    detectMathSession(cursoPosition.value)
  })
  hotkeys.setScope('editor')
}

const removeHotkeys = () => {
  hotkeys.deleteScope('editor')
}

const disposeEditor = () => {
  if (autoCompleterDisposable) {
    autoCompleterDisposable.dispose()
  }
  if (aiInlineEditInstance) {
    aiInlineEditInstance.dispose()
  }
  if (editor) {
    editor.dispose()
  }
}

const surveyTypes = computed(() => QUESTION_SURVEY_TYPES)
const testTypes = computed(() => QUESTION_TEST_TYPES)
const questionTypes = computed<QuestionType[]>(() => {
  return (surveyTypes.value || []).concat(testTypes.value || [])
})

const getQuestion = (): Question | undefined => {
  return qEditorState.question
}

const getQuestionTypes = () => questionTypes.value
const getSkills = () => skills.value

watch(isRevisionMode, () => {
  if (!editor) return
  editor.updateOptions({ readOnly: isRevisionMode.value })
})

const setupMonacoEditor = () => {
  const container = editorContainer.value
  if (!container || !monaco) return

  // Register QSyntax language
  registerQsyntaxLanguage()

  // Create editor
  editor = monaco.editor.create(container, {
    value: body.value,
    language: QSYNTAX_LANGUAGE_ID,
    theme: 'qsyntax-dark',
    automaticLayout: true,
    wordWrap: 'on',
    minimap: { enabled: false },
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    fontSize: 14,
    fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace",
    tabSize: 2,
    insertSpaces: true,
    renderWhitespace: 'selection',
    cursorBlinking: 'smooth',
    cursorSmoothCaretAnimation: 'on',
    smoothScrolling: true,
    padding: { top: 10, bottom: 10 },
    suggest: { showSnippets: true, snippetsPreventQuickSuggestions: false },
    quickSuggestions: { other: true, comments: false, strings: false }
  })

  // Setup AutoCompleter
  const autoCompleter = new MonacoAutoCompleter(
    () => getQuestion(),
    () => getQuestionTypes(),
    () => getSkills()
  )
  autoCompleterDisposable = autoCompleter.register()

  // Setup Indent Process (with tab-trigger snippet support)
  new MonacoIndentProcess(editor, () => {
    let allSnippets: any[] = []
    questionTypes.value.forEach(qt => {
      allSnippets = allSnippets.concat(qt.snippets)
    })
    return allSnippets
  })

  // Setup AI Inline Edit - only when enabled via school setting
  if (props.enableAiInlineEdit) {
    aiInlineEditInstance = new AIInlineEdit(editor, {
      examId: exam.value.id,
      promptId: selectedQuestionPromptId.value || undefined,
      onContentChange: (content: string) => {
        skipUpdateContent = 'skip_update'
        qEditorState.editBody({ body: content })
        updateCurrentLine()
        setTimeout(() => (skipUpdateContent = 'none'), 0)
      },
      getPrompts: () => promptForQuestions.value
    })
  }

  // Emit editor-ready event for parent components (e.g., AI Sidebar Chat)
  emit('editor-ready', editor)

  // Handle content changes
  editor.onDidChangeModelContent(() => {
    // While an AI inline-diff review is active, the model is a scratch buffer
    // (proposed lines shown in green). Don't sync to the store until the user
    // accepts; reject must leave the store unchanged.
    if ((editor as any).__aiDiffReviewing) return
    if (skipUpdateContent === 'skip_all') return
    if (skipUpdateContent === 'skip_dispatch') return

    const newBody = editor?.getValue() || ''
    skipUpdateContent = 'skip_update'
    qEditorState.editBody({ body: newBody })
    updateCurrentLine()
    setTimeout(() => (skipUpdateContent = 'none'), 0)
  })

  // Handle cursor position changes
  editor.onDidChangeCursorPosition((e) => {
    const position = e.position
    const newPosition = { row: position.lineNumber - 1, column: position.column - 1 }

    if (!isEqual(beforeCursoPosition, newPosition)) {
      beforeCursoPosition = newPosition
      qEditorState.commitEditCursoPosition({
        row: newPosition.row,
        column: newPosition.column
      })
    }
    updateCurrentLine()
  })

  // Handle selection changes
  editor.onDidChangeCursorSelection((e) => {
    const selection = e.selection
    qEditorState.commitEditRange({
      range: {
        start: { row: selection.startLineNumber - 1, column: selection.startColumn - 1 },
        end: { row: selection.endLineNumber - 1, column: selection.endColumn - 1 }
      } as any
    })
  })

  // Handle paste for image upload
  // Monaco uses an internal textarea for input - must attach listener there
  const monacoTextarea = container.querySelector('textarea.inputarea') as HTMLTextAreaElement
  if (monacoTextarea) {
    console.log('[PASTE] Found Monaco textarea, attaching listener')
    monacoTextarea.addEventListener('paste', onPaste as any, true)
  } else {
    console.log('[PASTE] Monaco textarea not found, using container + window fallback')
    container.addEventListener('paste', onPaste as any, true)
  }
  // Window-level fallback to catch paste anywhere when editor is focused
  window.addEventListener('paste', onPaste as any, true)
  container.addEventListener('dragover', (e) => e.preventDefault(), false)
  container.addEventListener('drop', (e) => e.preventDefault(), false)

  // Setup snippet handler
  editorEventBus.on('insertSnippet', (snippet: any) => handleSnippetInsert(snippet))


}

const handleSnippetInsert = (snippet: any) => {
  if (!editor || !monaco) return

  const selection = editor.getSelection()
  let selectedText = ''
  if (selection && !selection.isEmpty()) {
    selectedText = editor.getModel()?.getValueInRange(selection) || ''
  }
  const innerText = selectedText || '内容'

  let snippetContent = snippet.content

  switch (snippet.type) {
    case 'heading': snippetContent = `# ${innerText}\n`; break
    case 'bold': snippetContent = `**${innerText}**`; break
    case 'quote': snippetContent = `> ${innerText}\n`; break
    case 'link': snippetContent = `[${innerText}](http://)`; break
    case 'color': snippetContent = `<span style="color:${snippetContent}">${innerText}</span>`; break
    case 'list': snippetContent = `- ${innerText}\n`; break
    case 'fontsize': editor.updateOptions({ fontSize: snippet.fontSize }); return
    case 'mathlive':
      mathliveState.show('', (latex: string) => {
        if (editor && monaco) {
          const position = editor.getPosition()
          if (position) {
            editor.executeEdits('snippet', [{
              range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
              text: latexToMathLive(latex),
              forceMoveMarkers: true
            }])
          }
          editor.focus()
        }
      })
      return
  }

  if (selection) {
    editor.executeEdits('snippet', [{ range: selection, text: snippetContent, forceMoveMarkers: true }])
  }
}



const setMarker = () => {
  if (!editor || !monaco) return

  const model = editor.getModel()
  if (!model) return

  const decorations: monacoType.editor.IModelDeltaDecoration[] = []
  let line = 0

  questions.value.forEach((question) => {
    if (line > 0) {
      decorations.push({
        range: new monaco!.Range(line + 1, 1, line + 1, model.getLineMaxColumn(line + 1)),
        options: {
          isWholeLine: true,
          className: 'question-divider-decoration',
          glyphMarginClassName: 'question-glyph',
          glyphMarginHoverMessage: { value: `${question.index}つ目` }
        }
      })
      line += 1
    }
    line += question.length
  })

  markers = editor.deltaDecorations(markers, decorations)
}

const onPaste = async (event: ClipboardEvent) => {
  // Skip if paste target is inside the AI sidebar chat (textarea, input, etc.)
  const target = event.target as HTMLElement
  if (target && (
    target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' ||
    target.closest('.ai-sidebar-chat')
  )) {
    return
  }

  console.log('[PASTE DEBUG] onPaste fired!', {
    hasClipboardData: !!event.clipboardData,
    filesCount: event.clipboardData?.files?.length || 0,
    items: event.clipboardData?.items ? Array.from(event.clipboardData.items).map(i => ({ kind: i.kind, type: i.type })) : [],
    hasEditor: !!editor,
    hasMonaco: !!monaco
  })

  if (!event.clipboardData?.files[0] || !editor || !monaco) {
    console.log('[PASTE DEBUG] Early return - no file or no editor')
    return
  }

  const file = event.clipboardData.files[0]
  console.log('[PASTE DEBUG] File:', { name: file.name, type: file.type, size: file.size })

  // Only handle image/audio/video files
  if (!file.type.match(/^(image|audio|video)\//)) {
    console.log('[PASTE DEBUG] File type not supported:', file.type)
    return
  }

  // MUST preventDefault BEFORE async operations, otherwise Monaco's default handler runs
  event.preventDefault()
  event.stopPropagation()
  console.log('[PASTE DEBUG] preventDefault called')

  const position = editor.getPosition()
  if (!position) {
    console.log('[PASTE DEBUG] No cursor position')
    return
  }
  console.log('[PASTE DEBUG] Cursor at:', position.lineNumber, position.column)

  const uploadText = '![uploading image](/Spinner.gif)'
  const range = new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column)

  editor.executeEdits('paste-image', [{ range, text: uploadText, forceMoveMarkers: true }])
  console.log('[PASTE DEBUG] Placeholder inserted')

  try {
    console.log('[PASTE DEBUG] Starting upload...', { examId: exam.value?.id })
    const url = await ImageApi.upload(exam.value, file)
    console.log('[PASTE DEBUG] Upload success:', url)
    const urlLink = `![image](${url})`

    // Find and replace the upload placeholder
    const model = editor.getModel()
    if (model) {
      const fullText = model.getValue()
      const placeholderIndex = fullText.indexOf(uploadText)
      console.log('[PASTE DEBUG] Placeholder found at index:', placeholderIndex)
      if (placeholderIndex >= 0) {
        const startPos = model.getPositionAt(placeholderIndex)
        const endPos = model.getPositionAt(placeholderIndex + uploadText.length)
        const replaceRange = new monaco.Range(
          startPos.lineNumber, startPos.column,
          endPos.lineNumber, endPos.column
        )
        editor.executeEdits('paste-image', [{ range: replaceRange, text: urlLink, forceMoveMarkers: true }])
        console.log('[PASTE DEBUG] Replaced placeholder with:', urlLink)
      }
    }
  } catch (err) {
    console.error('[PASTE DEBUG] Upload FAILED:', err)
    // Remove placeholder on error
    const model = editor.getModel()
    if (model) {
      const fullText = model.getValue()
      const placeholderIndex = fullText.indexOf(uploadText)
      if (placeholderIndex >= 0) {
        const startPos = model.getPositionAt(placeholderIndex)
        const endPos = model.getPositionAt(placeholderIndex + uploadText.length)
        const replaceRange = new monaco.Range(
          startPos.lineNumber, startPos.column,
          endPos.lineNumber, endPos.column
        )
        editor.executeEdits('paste-image', [{ range: replaceRange, text: '', forceMoveMarkers: true }])
      }
    }
  }
}

watch(body, (value) => {
  if (skipUpdateContent === 'skip_all') return
  if (skipUpdateContent === 'skip_update') return
  if (!editor) return

  setMarker()
  const position = editor.getPosition()
  skipUpdateContent = 'skip_dispatch'
  editor.setValue(value)
  if (position) editor.setPosition(position)
  setTimeout(() => (skipUpdateContent = 'none'), 0)
})

watch(questions, () => {
  setMarker()
})

watch(cursoPosition, (value) => {
  if (!editor) return
  const monacoPosition = { lineNumber: value.row + 1, column: value.column + 1 }
  const currentPosition = editor.getPosition()
  if (!currentPosition || currentPosition.lineNumber !== monacoPosition.lineNumber || currentPosition.column !== monacoPosition.column) {
    beforeCursoPosition = value
    editor.setPosition(monacoPosition)
  }
})

const detectMathSession = (cursor: CursoPosition) => {
  if (!editor || !monaco) return
  const model = editor.getModel()
  if (!model) return

  const currentLine = model.getLineContent(cursor.row + 1)
  const matchMaths = detectMath(currentLine)

  for (const matchMath of matchMaths) {
    const mathCode = matchMath.mathCode
    if (cursor.column > matchMath.index && cursor.column < matchMath.index + mathCode.length) {
      const latex = mathLiveToLatex(mathCode)
      mathliveState.show(latex, (newLatex: string) => {
        const newLine = `${currentLine.slice(0, matchMath.index)}${latexToMathLive(newLatex)}${currentLine.slice(matchMath.index + mathCode.length)}`
        const range = new monaco!.Range(cursor.row + 1, 1, cursor.row + 1, currentLine.length + 1)
        editor?.executeEdits('math', [{ range, text: newLine, forceMoveMarkers: true }])
        editor?.focus()
      })
      return
    }
  }

  mathliveState.show('', (newLatex: string) => {
    if (newLatex && editor && monaco) {
      const position = editor.getPosition()
      if (position) {
        editor.executeEdits('math', [{
          range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
          text: latexToMathLive(newLatex),
          forceMoveMarkers: true
        }])
        editor.focus()
      }
    }
  })
}

watch(currentPage, (value) => {
  if (!editor || !monaco) return
  if (editor.hasTextFocus()) return

  let bodyText = body.value.replace(/\n===+\n/g, '\n---\n').split(/\n---+\n/).splice(0, value - 1).join('\n---\n')
  let row = bodyText === '' ? 0 : bodyText.split('\n').length + 1
  row = row === 1 ? 0 : row

  editor.focus()
  const monacoLine = Math.max(row, 1)
  editor.revealLineInCenter(monacoLine)
  setTimeout(() => {
    editor?.setPosition({ lineNumber: monacoLine, column: 1 })
    editor?.setSelection(new monaco!.Range(monacoLine, 1, monacoLine, 1))
  }, 0)
})

const updateCurrentLine = () => {
  if (!editor) return
  const position = editor.getPosition()
  if (!position) return
  const model = editor.getModel()
  if (!model) return

  const currentLine = position.lineNumber
  const content = model.getLineContent(currentLine)
  const previousLineContent = currentLine > 1 ? model.getLineContent(currentLine - 1) : ''

  qEditorState.commitSetCurrentLine({ content, previousLineContent })
}

const focus = () => {
  editor?.focus()
}

const onDrop = async (event: DragEvent) => {
  if (!event.dataTransfer?.files.length || !editor || !monaco) return

  const file = event.dataTransfer.files[0]
  const position = editor.getPosition()
  if (!position) return

  const uploadText = `![uploading ${file.name}](/Spinner.gif)`
  const range = new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column)

  editor.executeEdits('drop-image', [{
    range,
    text: uploadText,
    forceMoveMarkers: true
  }])

  const url = await ImageApi.upload(exam.value, file)
  const urlLink = `![${file.name}](${url})`

  const currentPos = editor.getPosition()
  if (currentPos) {
    const endColumn = position.column + uploadText.length
    const replaceRange = new monaco.Range(
      position.lineNumber,
      position.column,
      position.lineNumber,
      endColumn
    )
    editor.executeEdits('drop-image', [{
      range: replaceRange,
      text: urlLink,
      forceMoveMarkers: true
    }])
  }
}

__expose({ focus })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createCommentVNode(" Loading state "),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "d-flex flex-column align-items-center" }, [
              _createElementVNode("div", {
                class: "spinner-border text-primary",
                role: "status"
              }, [
                _createElementVNode("span", { class: "visually-hidden" }, "Loading editor...")
              ]),
              _createElementVNode("div", { class: "mt-2 text-muted" }, "エディターを読み込み中...")
            ], -1 /* CACHED */)
          ]))]))
        : (error.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createCommentVNode(" Error state "),
              _createElementVNode("div", _hoisted_4, [
                _cache[1] || (_cache[1] = _createElementVNode("strong", null, "エディターの読み込みに失敗しました", -1 /* CACHED */)),
                _createElementVNode("p", _hoisted_5, _toDisplayString(error.value), 1 /* TEXT */),
                _createElementVNode("button", {
                  class: "btn btn-outline-danger btn-sm mt-2",
                  onClick: retryLoad
                }, "再読み込み")
              ])
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
          : _createCommentVNode("v-if", true),
      _createCommentVNode(" Editor container (rendered by Monaco) "),
      _withDirectives(_createElementVNode("div", {
        id: __props.editorId,
        ref_key: "editorContainer",
        ref: editorContainer,
        class: "monaco-editor-container",
        onDrop: onDrop
      }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_6), [
        [_vShow, !loading.value && !error.value]
      ])
    ])
  ]))
}
}

})